import { axiosBasic } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { getSelectedCompany } from "../../Global/CustomFunctions";
import { createDefaultTemplate, normalizeTemplate } from "./payslipTemplateDefaults";

const { SalaryComponent, TaxPolicy, PayrollStructure, Company, PayslipTemplate } = config.REST_API;

const STORAGE_PREFIX = "hrm:payslipTemplate:";

/** @param {import('axios').AxiosResponse} res */
const unwrapArray = (res) => {
  const body = res?.data;
  if (body == null) return [];
  if (body.success === false) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.items)) return body.items;
  return [];
};

/** @param {import('axios').AxiosResponse} res */
const unwrapOne = (res) => {
  const body = res?.data;
  if (body == null) return null;
  if (body.success === false) return null;
  if (body.data && typeof body.data === "object") return body.data;
  if (typeof body === "object" && !Array.isArray(body)) return body;
  return null;
};

export const getCompanyId = () => {
  const selected = getSelectedCompany();
  if (selected && typeof selected === "object") {
    return String(selected.id ?? selected.Id ?? selected.companyId ?? selected.CompanyId ?? "").trim();
  }
  return "";
};

export const getCompanyName = () => {
  const selected = getSelectedCompany();
  if (selected && typeof selected === "object") {
    return String(selected.name ?? selected.Name ?? "").trim();
  }
  return "";
};

/** @param {Record<string, unknown>} data */
const getLogoUrlFromSettings = (data) => {
  if (!data || typeof data !== "object") return "";
  const raw =
    data.companyLogoUrl ||
    data.CompanyLogoUrl ||
    data.logo ||
    data.Logo ||
    data.logoUrl ||
    data.LogoUrl;
  return raw != null ? String(raw).trim() : "";
};

export const loadCompanyLogo = async () => {
  try {
    const selected = getSelectedCompany();
    if (selected && typeof selected === "object") {
      const stored = selected.logo != null ? String(selected.logo).trim() : "";
      if (stored) return stored;
    }
    const res = await axiosBasic.get(Company.GetSettings);
    const { success, data } = res.data || {};
    if (success && data) return getLogoUrlFromSettings(data);
  } catch {
    /* fallback empty */
  }
  return "";
};

/** Salary component kind — matches SalaryComponentsPage */
export const COMPONENT_KIND = {
  Earning: 0,
  Deduction: 1,
  StatutoryDeduction: 2,
  EmployerContribution: 3,
  Other: 4,
};

/** @param {unknown} kind */
const normalizeKind = (kind) => {
  if (typeof kind === "number" && kind >= 0 && kind <= 4) return kind;
  const raw = String(kind ?? "").trim().toLowerCase();
  if (raw === "1" || raw.includes("deduct") && !raw.includes("statutory")) return COMPONENT_KIND.Deduction;
  if (raw.includes("statutory")) return COMPONENT_KIND.StatutoryDeduction;
  if (raw === "3" || raw.includes("employer")) return COMPONENT_KIND.EmployerContribution;
  if (raw === "4" || raw.includes("other")) return COMPONENT_KIND.Other;
  return COMPONENT_KIND.Earning;
};

/** @param {Record<string, unknown>} raw */
const normalizeComponent = (raw) => {
  const get = (a, b) => (raw[a] !== undefined && raw[a] !== null ? raw[a] : raw[b]);
  return {
    id: String(get("id", "Id") ?? get("salaryComponentId", "SalaryComponentId") ?? ""),
    name: String(get("name", "Name") ?? ""),
    code: String(get("code", "Code") ?? ""),
    kind: normalizeKind(get("kind", "Kind")),
    officeName: String(get("officeName", "OfficeName") ?? ""),
    officeCurrency: String(get("officeCurrency", "OfficeCurrency") ?? ""),
    isActive: get("isActive", "IsActive") !== false,
  };
};

/** @param {Record<string, unknown>} raw */
const normalizeTaxPolicy = (raw) => {
  const get = (a, b) => (raw[a] !== undefined && raw[a] !== null ? raw[a] : raw[b]);
  return {
    id: String(get("id", "Id") ?? ""),
    name: String(get("name", "Name") ?? get("policyName", "PolicyName") ?? ""),
    officeName: String(get("officeName", "OfficeName") ?? ""),
    isActive: get("isActive", "IsActive") !== false,
  };
};

/** @param {Record<string, unknown>} raw */
const normalizeStructure = (raw) => {
  const get = (a, b) => (raw[a] !== undefined && raw[a] !== null ? raw[a] : raw[b]);
  return {
    id: String(get("id", "Id") ?? ""),
    name: String(get("name", "Name") ?? ""),
    status: String(get("status", "Status") ?? "").toLowerCase(),
    isActive: get("isActive", "IsActive") !== false,
  };
};

/** @returns {Promise<{ earnings: ReturnType<typeof normalizeComponent>[], deductions: ReturnType<typeof normalizeComponent>[], taxes: ReturnType<typeof normalizeTaxPolicy>[], structures: ReturnType<typeof normalizeStructure>[] }>} */
export const fetchPayslipPayrollData = async () => {
  const [compRes, taxRes, structRes] = await Promise.all([
    axiosBasic.get(SalaryComponent.Get).catch(() => ({ data: [] })),
    axiosBasic.get(TaxPolicy.Get, { params: { page: 1, take: 200 } }).catch(() => ({ data: [] })),
    axiosBasic.get(PayrollStructure.Get).catch(() => ({ data: [] })),
  ]);

  const components = unwrapArray(compRes)
    .map((r) => normalizeComponent(r))
    .filter((c) => c.id && c.isActive);

  const earnings = components.filter((c) => c.kind === COMPONENT_KIND.Earning);
  const deductions = components.filter(
    (c) =>
      c.kind === COMPONENT_KIND.Deduction ||
      c.kind === COMPONENT_KIND.StatutoryDeduction
  );

  const taxes = unwrapArray(taxRes)
    .map((r) => normalizeTaxPolicy(r))
    .filter((t) => t.id && t.isActive);

  const structures = unwrapArray(structRes)
    .map((r) => normalizeStructure(r))
    .filter((s) => s.id && s.isActive && s.status !== "archived");

  return { earnings, deductions, taxes, structures };
};

const storageKey = (companyId) => `${STORAGE_PREFIX}${companyId}`;

/** @param {string} companyId */
export const loadTemplateFromStorage = (companyId) => {
  if (!companyId) return createDefaultTemplate();
  try {
    const raw = localStorage.getItem(storageKey(companyId));
    if (!raw) return createDefaultTemplate();
    return normalizeTemplate(JSON.parse(raw));
  } catch {
    return createDefaultTemplate();
  }
};

/** @param {string} companyId @param {import('./payslipTemplateDefaults').PayslipTemplate} template */
export const saveTemplateToStorage = (companyId, template) => {
  if (!companyId) return;
  localStorage.setItem(storageKey(companyId), JSON.stringify(template));
};

/** @param {string} companyId */
export const loadPayslipTemplate = async (companyId) => {
  if (!companyId) return createDefaultTemplate();
  if (PayslipTemplate?.Get) {
    try {
      const res = await axiosBasic.get(PayslipTemplate.Get);
      const data = unwrapOne(res);
      if (data) return normalizeTemplate(data);
    } catch {
      /* API may not exist yet — use local storage */
    }
  }
  return loadTemplateFromStorage(companyId);
};

/** @param {import('./payslipTemplateDefaults').PayslipTemplate} template */
export const savePayslipTemplate = async (companyId, template) => {
  saveTemplateToStorage(companyId, template);
  if (PayslipTemplate?.Save) {
    try {
      await axiosBasic.post(PayslipTemplate.Save, { companyId, ...template });
    } catch {
      /* persisted locally */
    }
  }
};

/** @param {import('./payslipTemplateDefaults').PayslipTemplate} template */
export const finalizePayslipTemplate = async (companyId, template) => {
  const finalized = {
    ...template,
    finalized: true,
    finalizedAt: new Date().toISOString(),
  };
  saveTemplateToStorage(companyId, finalized);
  if (PayslipTemplate?.Finalize) {
    try {
      await axiosBasic.post(PayslipTemplate.Finalize, { companyId, ...finalized });
    } catch {
      /* persisted locally */
    }
  }
  return finalized;
};

/** Deterministic sample amount for preview rows */
export const sampleAmount = (seed, base = 5000) => {
  let h = 0;
  const s = String(seed || "x");
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 9973;
  const n = base + (h % 15000);
  return n;
};

export const formatMoney = (value, currency = "") => {
  const n = Number(value);
  const formatted = Number.isFinite(n)
    ? n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : "0";
  return currency ? `${formatted} ${currency}` : formatted;
};
